import { action, computed, makeAutoObservable, observable } from 'mobx';
import { v4 as uuid } from 'uuid';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import {
    mapPopupFromApi,
    mapPopupPayloadToApi,
    mapTriggerCreatePayloadFromForm,
    mapTriggerFromApi,
    mapTriggerTemplateFromApi,
    mapTriggerUpdatePayloadFromForm,
    mapTriggerToFormValues,
} from './MarkersOfHarmPopupTab.mappers';
import {
    buildMarkersOfHarmAutomationDto,
    hydrateAppliedActionSettingsFromAutomation,
    MarkersOfHarmAppliedActionSettingsType,
} from './MarkersOfHarmPopupAutomation.mapper';
import { ComplianceAutomationType } from 'src/api_openapi/v2/compliance/automationSchemas';
import { MarkersOfHarmPopupFormState } from './MarkersOfHarmPopupForm.state';
import {
    MarkersOfHarmPopupFilterType,
    MarkersOfHarmPopupSortType,
    MarkersOfHarmPopupType,
    PopupRiskLevelType,
    PopupTriggerSettingsFormType,
    PopupTriggerTemplateType,
} from './MarkersOfHarmPopupTab.types';

const createMohIdentifier = (): string => `moh-${uuid()}`;
const createPopupReference = (triggerId: number): string => `moh-${triggerId}`;

const getBeErrorMessage = (responseBody: unknown): string | null => {
    if (typeof responseBody !== 'object' || responseBody === null) {
        return null;
    }

    const bodyRecord = Object.fromEntries(Object.entries(responseBody));
    const messageValue = bodyRecord.message;

    if (typeof messageValue === 'string' && messageValue.trim() !== '') {
        return messageValue;
    }

    return null;
};

class CompensationStack {
    private readonly compensations: Array<() => Promise<boolean>> = [];

    public push(compensation: () => Promise<boolean>): void {
        this.compensations.push(compensation);
    }

    public async rollback(): Promise<boolean> {
        let allSucceeded = true;

        for (const compensation of [...this.compensations].reverse()) {
            const succeeded = await compensation();
            if (!succeeded) {
                allSucceeded = false;
            }
        }

        return allSucceeded;
    }
}

const getLastActiveTimestamp = (popup: MarkersOfHarmPopupType): number | null => {
    if (popup.active && popup.activationDate !== undefined) {
        return new Date(popup.activationDate).getTime();
    }

    if (!popup.active && popup.deactivationDate !== undefined) {
        return new Date(popup.deactivationDate).getTime();
    }

    return null;
};

const comparePopupsBySort = (
    a: MarkersOfHarmPopupType,
    b: MarkersOfHarmPopupType,
    sortBy: MarkersOfHarmPopupSortType
): number => {
    switch (sortBy) {
        case 'title-asc':
            return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
        case 'title-desc':
            return b.title.localeCompare(a.title, undefined, { sensitivity: 'base' });
        case 'last-active-date': {
            const aTimestamp = getLastActiveTimestamp(a);
            const bTimestamp = getLastActiveTimestamp(b);

            if (aTimestamp === null && bTimestamp === null) {
                return 0;
            }
            if (aTimestamp === null) {
                return 1;
            }
            if (bTimestamp === null) {
                return -1;
            }

            return bTimestamp - aTimestamp;
        }
        case 'creation-date':
        default:
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
};

const filterPopupsByStatus = (
    popups: MarkersOfHarmPopupType[],
    activeFilter: MarkersOfHarmPopupFilterType
): MarkersOfHarmPopupType[] => {
    if (activeFilter === 'active') {
        return popups.filter((popup) => popup.active);
    }

    if (activeFilter === 'inactive') {
        return popups.filter((popup) => !popup.active);
    }

    return popups;
};

export class MarkersOfHarmPopupTabState {
    @observable public isSaving = false;
    @observable public errorMessage: string | null = null;
    @observable public selectedPopupId: string | null = null;
    @observable public activeFilter: MarkersOfHarmPopupFilterType = 'show-all';
    @observable public sortBy: MarkersOfHarmPopupSortType = 'creation-date';
    @observable public triggerTemplates: PopupTriggerTemplateType[] = [];
    private readonly popupsResource: Resource<MarkersOfHarmPopupType[]>;

    public constructor(private readonly trpcClient: TrpcClient) {
        this.popupsResource = new Resource(async (): Promise<MarkersOfHarmPopupType[]> => {
            const [popupResponse, triggerResponse, triggerTemplatesResponse] = await Promise.all([
                this.trpcClient.client.markersOfHarmPopup.getPopups.query(),
                this.trpcClient.client.markersOfHarmTrigger.getTriggers.query(),
                this.trpcClient.client.markersOfHarmTrigger.getTriggerTemplates.query(),
            ]);

            if (popupResponse.status !== 200) {
                throw new Error(`Failed to load Markers of Harm Pop-Ups (${popupResponse.status})`);
            }
            if (triggerResponse.status !== 200) {
                throw new Error(`Failed to load Markers of Harm triggers (${triggerResponse.status})`);
            }
            if (triggerTemplatesResponse.status !== 200) {
                throw new Error(
                    `Failed to load Markers of Harm trigger templates (${triggerTemplatesResponse.status})`
                );
            }

            this.triggerTemplates = triggerTemplatesResponse.body.map(mapTriggerTemplateFromApi);

            const triggers = triggerResponse.body.map(mapTriggerFromApi);
            const triggerById = new Map(triggers.map((trigger) => [trigger.id, trigger]));
            const triggerIdByName = new Map(triggers.map((trigger) => [trigger.name, trigger.id]));

            return popupResponse.body.map((popup) => {
                const mappedPopup = mapPopupFromApi(popup, triggerIdByName);
                const trigger = triggerById.get(mappedPopup.triggerId);
                return {
                    ...mappedPopup,
                    trigger,
                };
            });
        });
        makeAutoObservable(this);
    }

    @computed public get isLoading(): boolean {
        return this.popupsResource.get().type === 'loading';
    }

    @computed public get popups(): MarkersOfHarmPopupType[] {
        const resource = this.popupsResource.get();
        if (resource.type === 'ready') {
            return resource.value;
        }
        return [];
    }

    @computed public get filteredAndSortedPopups(): MarkersOfHarmPopupType[] {
        const filtered = filterPopupsByStatus(this.popups, this.activeFilter);
        return [...filtered].sort((a, b) => comparePopupsBySort(a, b, this.sortBy));
    }

    @action public setActiveFilter = (filter: MarkersOfHarmPopupFilterType): void => {
        this.activeFilter = filter;
    };

    @action public setSortBy = (sortBy: MarkersOfHarmPopupSortType): void => {
        this.sortBy = sortBy;
    };

    @action public refreshPopups = async (): Promise<void> => {
        await this.popupsResource.refresh();
    };

    @action public selectPopupForEdit = (popupId: string): void => {
        this.selectedPopupId = popupId;
    };

    @action public clearSelectedPopup = (): void => {
        this.selectedPopupId = null;
    };

    @action public setError = (message: string | null): void => {
        this.errorMessage = message;
    };

    private getAutomationNameFromPopup = (popup: MarkersOfHarmPopupType): string | null => {
        return popup.trigger?.actions[0]?.name ?? popup.trigger?.name ?? null;
    };

    @computed public get popupAutomationNameById(): Map<string, string | null> {
        return new Map(this.popups.map((popup) => [popup.id, this.getAutomationNameFromPopup(popup)]));
    }

    public getPopupAutomationName = (popup: MarkersOfHarmPopupType): string | null =>
        this.popupAutomationNameById.get(popup.id) ?? this.getAutomationNameFromPopup(popup);

    @action public createPopup = async (
        payload: Omit<MarkersOfHarmPopupType, 'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger'>
    ): Promise<MarkersOfHarmPopupType | null> => {
        this.isSaving = true;
        this.setError(null);

        try {
            return await this.persistCreatePopup(payload);
        } finally {
            this.isSaving = false;
        }
    };

    private persistCreatePopup = async (
        payload: Omit<MarkersOfHarmPopupType, 'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger'>
    ): Promise<MarkersOfHarmPopupType | null> => {
        try {
            const response = await this.trpcClient.client.markersOfHarmPopup.createPopup.mutate({
                data: mapPopupPayloadToApi(payload),
            });

            if (response.status !== 200) {
                this.setError('Could not create Pop-Up. Please try again.');
                return null;
            }

            return mapPopupFromApi(response.body);
        } catch (error) {
            console.error(error);
            this.setError('Could not create Pop-Up. Please try again.');
            return null;
        }
    };

    @action public updatePopup = async (
        id: string,
        payload: Omit<MarkersOfHarmPopupType, 'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger'>
    ): Promise<MarkersOfHarmPopupType | null> => {
        this.isSaving = true;
        this.setError(null);

        try {
            return await this.persistUpdatePopup(id, payload);
        } finally {
            this.isSaving = false;
        }
    };

    private persistUpdatePopup = async (
        id: string,
        payload: Omit<MarkersOfHarmPopupType, 'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger'>
    ): Promise<MarkersOfHarmPopupType | null> => {
        const numericId = parseInt(id, 10);

        if (isNaN(numericId)) {
            this.setError('Invalid Pop-Up id.');
            return null;
        }

        try {
            const response = await this.trpcClient.client.markersOfHarmPopup.updatePopup.mutate({
                id: numericId,
                data: mapPopupPayloadToApi(payload),
            });

            if (response.status !== 200) {
                this.setError('Could not update Pop-Up. Please try again.');
                return null;
            }

            return mapPopupFromApi(response.body);
        } catch (error) {
            console.error(error);
            this.setError('Could not update Pop-Up. Please try again.');
            return null;
        }
    };

    @action public createTriggerForPopup = async (
        triggerSettings: PopupTriggerSettingsFormType,
        automationName: string,
        riskLevel: PopupRiskLevelType,
        enabled = true
    ): Promise<number | null> => {
        this.setError(null);

        try {
            const payload = mapTriggerCreatePayloadFromForm(triggerSettings, automationName, riskLevel, enabled);
            const response = await this.trpcClient.client.markersOfHarmTrigger.createTrigger.mutate({
                data: payload,
            });

            if (response.status !== 201) {
                const apiErrorMessage = getBeErrorMessage(response.body);
                if (apiErrorMessage !== null) {
                    this.setError(apiErrorMessage);
                    return null;
                }
                this.setError('Could not create trigger for this Pop-Up.');
                return null;
            }

            return response.body.id;
        } catch (error) {
            console.error(error);
            this.setError(error instanceof Error ? error.message : 'Could not create trigger for this Pop-Up.');
            return null;
        }
    };

    @action public updateTriggerForPopup = async (
        triggerId: number,
        triggerSettings: PopupTriggerSettingsFormType,
        automationName: string,
        riskLevel: PopupRiskLevelType,
        enabled: boolean
    ): Promise<boolean> => {
        this.setError(null);

        const updated = await this.persistUpdateTriggerForPopup(
            triggerId,
            triggerSettings,
            automationName,
            riskLevel,
            enabled
        );

        if (updated) {
            return true;
        }

        this.setError('Could not update trigger for this Pop-Up.');
        return false;
    };

    private persistUpdateTriggerForPopup = async (
        triggerId: number,
        triggerSettings: PopupTriggerSettingsFormType,
        automationName: string,
        riskLevel: PopupRiskLevelType,
        enabled: boolean
    ): Promise<boolean> => {
        try {
            const payload = mapTriggerUpdatePayloadFromForm(triggerSettings, automationName, riskLevel, enabled);
            const response = await this.trpcClient.client.markersOfHarmTrigger.updateTrigger.mutate({
                id: triggerId,
                data: payload,
            });

            if (response.status !== 200) {
                const apiErrorMessage = getBeErrorMessage(response.body);
                if (apiErrorMessage !== null) {
                    this.setError(apiErrorMessage);
                }
                return false;
            }

            return true;
        } catch (error) {
            console.error(error);
            this.setError(error instanceof Error ? error.message : 'Could not update trigger for this Pop-Up.');
            return false;
        }
    };

    private persistDisableTriggerForRollback = async (triggerId: number): Promise<boolean> => {
        try {
            const response = await this.trpcClient.client.markersOfHarmTrigger.updateTrigger.mutate({
                id: triggerId,
                data: { enabled: false },
            });
            return response.status === 200;
        } catch (error) {
            console.error('persistDisableTriggerForRollback failed', error);
            return false;
        }
    };

    @action public setPopupActive = async (popup: MarkersOfHarmPopupType, active: boolean): Promise<boolean> => {
        this.isSaving = true;
        this.setError(null);

        const previousActive = popup.active;
        const automationName = this.getAutomationNameFromPopup(popup);
        let triggerUpdated = false;
        let automationUpdated = false;
        let previousAutomation: ComplianceAutomationType | null = null;

        try {
            if (popup.trigger !== undefined) {
                const triggerResponse = await this.trpcClient.client.markersOfHarmTrigger.updateTrigger.mutate({
                    id: popup.trigger.id,
                    data: { enabled: active },
                });

                if (triggerResponse.status !== 200) {
                    this.setError('Could not update trigger status.');
                    return false;
                }
                triggerUpdated = true;
            }

            previousAutomation = automationName === null ? null : await this.getComplianceAutomation(automationName);
            if (automationName !== null && previousAutomation !== null) {
                const automationSaved = await this.updateComplianceAutomation({
                    ...previousAutomation,
                    enabled: active,
                    actions: previousAutomation.actions.map((action) => ({
                        ...action,
                        enabled: active,
                    })),
                });

                if (!automationSaved) {
                    if (triggerUpdated && popup.trigger !== undefined) {
                        await this.trpcClient.client.markersOfHarmTrigger.updateTrigger.mutate({
                            id: popup.trigger.id,
                            data: { enabled: previousActive },
                        });
                    }
                    return false;
                }
                automationUpdated = true;
            }

            const updatedPopup = await this.persistUpdatePopup(popup.id, {
                title: popup.title,
                bodyText: popup.bodyText,
                primaryButton: popup.primaryButton,
                secondaryButton: popup.secondaryButton,
                triggerId: popup.triggerId,
                active,
            });

            if (updatedPopup === null) {
                if (automationUpdated && previousAutomation !== null) {
                    await this.updateComplianceAutomation(previousAutomation);
                }
                if (triggerUpdated && popup.trigger !== undefined) {
                    await this.trpcClient.client.markersOfHarmTrigger.updateTrigger.mutate({
                        id: popup.trigger.id,
                        data: { enabled: previousActive },
                    });
                }
                this.setError('Could not update Pop-Up status.');
                return false;
            }

            await this.refreshPopups();
            return true;
        } catch (error) {
            console.error(error);
            if (automationUpdated && previousAutomation !== null) {
                try {
                    await this.updateComplianceAutomation(previousAutomation);
                } catch (rollbackError) {
                    console.error(rollbackError);
                }
            }
            if (triggerUpdated && popup.trigger !== undefined) {
                try {
                    await this.trpcClient.client.markersOfHarmTrigger.updateTrigger.mutate({
                        id: popup.trigger.id,
                        data: { enabled: previousActive },
                    });
                } catch (rollbackError) {
                    console.error(rollbackError);
                }
            }
            this.setError('Could not update Pop-Up status.');
            return false;
        } finally {
            this.isSaving = false;
        }
    };

    @action public getPopupWithTriggerFromMoh = async (
        popup: MarkersOfHarmPopupType
    ): Promise<MarkersOfHarmPopupType> => {
        try {
            const response = await this.trpcClient.client.markersOfHarmTrigger.getTriggers.query({
                id: popup.triggerId,
            });

            if (response.status !== 200) {
                return popup;
            }

            const trigger = response.body.map(mapTriggerFromApi).find((item) => item.id === popup.triggerId);
            if (trigger === undefined) {
                return popup;
            }

            return {
                ...popup,
                trigger,
            };
        } catch (error) {
            console.error(error);
            return popup;
        }
    };

    @action public getComplianceAutomation = async (name: string): Promise<ComplianceAutomationType | null> => {
        try {
            const response = await this.trpcClient.client.complianceAutomation.getAutomation.query({ name });

            if (response.status === 200) {
                return response.body;
            }

            return null;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action private createComplianceAutomation = async (automation: ComplianceAutomationType): Promise<boolean> => {
        try {
            const response = await this.trpcClient.client.complianceAutomation.createAutomation.mutate({
                data: automation,
            });

            if (response.status === 200) {
                return true;
            }

            this.setError('Could not create compliance automation for this Pop-Up.');
            return false;
        } catch (error) {
            console.error(error);
            this.setError('Could not create compliance automation for this Pop-Up.');
            return false;
        }
    };

    @action private updateComplianceAutomation = async (
        automation: ComplianceAutomationType,
        resourceName: string = automation.name
    ): Promise<boolean> => {
        try {
            const response = await this.trpcClient.client.complianceAutomation.updateAutomation.mutate({
                name: resourceName,
                data: automation,
            });

            if (response.status === 200) {
                return true;
            }

            this.setError('Could not update compliance automation for this Pop-Up.');
            return false;
        } catch (error) {
            console.error(error);
            this.setError('Could not update compliance automation for this Pop-Up.');
            return false;
        }
    };

    private persistUpdateComplianceForRollback = async (
        automation: ComplianceAutomationType,
        resourceName: string = automation.name
    ): Promise<boolean> => {
        try {
            const response = await this.trpcClient.client.complianceAutomation.updateAutomation.mutate({
                name: resourceName,
                data: automation,
            });

            return response.status === 200;
        } catch (error) {
            console.error('persistUpdateComplianceForRollback failed', error);
            return false;
        }
    };

    @action private deleteComplianceAutomation = async (name: string): Promise<boolean> => {
        try {
            const response = await this.trpcClient.client.complianceAutomation.deleteAutomation.mutate({ name });

            if (response.status === 200 || response.status === 404) {
                return true;
            }

            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    private runRollback = async (compensations: CompensationStack, fallbackErrorMessage: string): Promise<void> => {
        const rollbackSucceeded = await compensations.rollback();
        const message = this.errorMessage ?? fallbackErrorMessage;

        if (rollbackSucceeded) {
            this.setError(message);
            return;
        }

        this.setError(`${message} Rollback may be incomplete — refresh and verify the configuration.`);
    };

    @action private upsertComplianceAutomation = async (
        previousAutomation: ComplianceAutomationType | null,
        automation: ComplianceAutomationType,
        compensations: CompensationStack
    ): Promise<boolean> => {
        if (previousAutomation !== null) {
            compensations.push(async () => this.persistUpdateComplianceForRollback(previousAutomation));
            return this.updateComplianceAutomation(automation);
        }

        const created = await this.createComplianceAutomation(automation);
        if (created) {
            compensations.push(async () => this.deleteComplianceAutomation(automation.name));
        }

        return created;
    };

    @action private saveTriggerForPopup = async ({
        editingTriggerId,
        previousPopup,
        previousAutomationName,
        triggerSettings,
        automationName,
        riskLevel,
        active,
        compensations,
    }: {
        editingTriggerId: number | null;
        previousPopup: MarkersOfHarmPopupType;
        previousAutomationName: string;
        triggerSettings: PopupTriggerSettingsFormType;
        automationName: string;
        riskLevel: PopupRiskLevelType;
        active: boolean;
        compensations: CompensationStack;
    }): Promise<number | null> => {
        if (editingTriggerId !== null) {
            const previousTrigger = previousPopup.trigger;
            if (previousTrigger !== undefined) {
                const previousTriggerTemplate =
                    this.triggerTemplates.find((template) => template.id === previousTrigger.templateId) ?? null;
                const previousTriggerSettings = mapTriggerToFormValues(previousTrigger, previousTriggerTemplate);
                if (previousTriggerSettings !== null) {
                    compensations.push(async () =>
                        this.persistUpdateTriggerForPopup(
                            editingTriggerId,
                            previousTriggerSettings,
                            previousAutomationName,
                            previousTrigger.riskLevel,
                            previousPopup.active
                        )
                    );
                }
            }

            const triggerSaved = await this.updateTriggerForPopup(
                editingTriggerId,
                triggerSettings,
                automationName,
                riskLevel,
                active
            );

            return triggerSaved ? editingTriggerId : null;
        }

        // Edit flow should never create a new trigger.
        // Missing trigger id means invalid state - stop the save.
        return null;
    };

    @action public savePopupFromForm = async (formState: MarkersOfHarmPopupFormState): Promise<boolean> => {
        if (!formState.validateForm()) {
            return false;
        }

        const payload = formState.buildPopupPayload();
        const triggerSettings = formState.buildTriggerSettingsFormValues();
        const appliedActionSettings = formState.buildAppliedActionSettings();
        const riskLevel = formState.riskLevel;

        if (triggerSettings === null) {
            this.setError('Trigger settings are required.');
            return false;
        }

        if (appliedActionSettings === null) {
            this.setError('Applied action is required.');
            return false;
        }

        const newIdentifier = createMohIdentifier();
        const automationName = formState.editingAutomationName ?? newIdentifier;

        this.isSaving = true;
        this.setError(null);

        try {
            if (formState.isEditMode && formState.editingPopupId !== null) {
                return await this.updatePopupWithDependencies({
                    formState,
                    popupId: formState.editingPopupId,
                    payload,
                    triggerSettings,
                    appliedActionSettings,
                    riskLevel,
                });
            }

            return await this.createPopupWithDependencies({
                payload,
                triggerSettings,
                automationName,
                appliedActionSettings,
                riskLevel,
            });
        } finally {
            this.isSaving = false;
        }
    };

    @action private createPopupWithDependencies = async ({
        payload,
        triggerSettings,
        automationName,
        appliedActionSettings,
        riskLevel,
    }: {
        payload: Omit<
            MarkersOfHarmPopupType,
            'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger' | 'triggerId'
        >;
        triggerSettings: PopupTriggerSettingsFormType;
        automationName: string;
        appliedActionSettings: MarkersOfHarmAppliedActionSettingsType;
        riskLevel: PopupRiskLevelType;
    }): Promise<boolean> => {
        const compensations = new CompensationStack();

        const createdTriggerId = await this.createTriggerForPopup(
            triggerSettings,
            automationName,
            riskLevel,
            payload.active
        );

        if (createdTriggerId === null) {
            await this.runRollback(
                compensations,
                'Could not create trigger for this Pop-Up. Changes were rolled back.'
            );
            return false;
        }

        compensations.push(async () => this.persistDisableTriggerForRollback(createdTriggerId));

        const automationDto = buildMarkersOfHarmAutomationDto({
            automationName,
            popupReference: createPopupReference(createdTriggerId),
            popupTitle: payload.title,
            active: payload.active,
            appliedActionSettings,
        });

        if (automationDto === null) {
            this.setError('Applied action configuration is incomplete.');
            await this.runRollback(
                compensations,
                'Could not build automation for this Pop-Up. Changes were rolled back.'
            );
            return false;
        }

        const automationCreated = await this.createComplianceAutomation(automationDto);
        if (!automationCreated) {
            await this.runRollback(
                compensations,
                'Could not create compliance automation for this Pop-Up. Changes were rolled back.'
            );
            return false;
        }

        compensations.push(async () => this.deleteComplianceAutomation(automationName));

        const createdPopup = await this.persistCreatePopup({
            ...payload,
            triggerId: createdTriggerId,
        });

        if (createdPopup === null) {
            await this.runRollback(compensations, 'Could not create Pop-Up. Changes were rolled back.');
            return false;
        }

        await this.refreshPopups();
        return true;
    };

    @action private updatePopupWithDependencies = async ({
        formState,
        popupId,
        payload,
        triggerSettings,
        appliedActionSettings,
        riskLevel,
    }: {
        formState: MarkersOfHarmPopupFormState;
        popupId: string;
        payload: Omit<
            MarkersOfHarmPopupType,
            'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger' | 'triggerId'
        >;
        triggerSettings: PopupTriggerSettingsFormType;
        appliedActionSettings: MarkersOfHarmAppliedActionSettingsType;
        riskLevel: PopupRiskLevelType;
    }): Promise<boolean> => {
        const cachedPopup = this.popups.find((popup) => popup.id === popupId);
        if (cachedPopup === undefined) {
            this.setError('Could not find Pop-Up to update.');
            return false;
        }

        const previousPopup = await this.getPopupWithTriggerFromMoh(cachedPopup);
        const previousAutomationName =
            formState.editingAutomationName ?? this.getAutomationNameFromPopup(previousPopup);
        if (previousAutomationName === null) {
            this.setError('Could not resolve previous automation name.');
            return false;
        }
        const previousAutomation = await this.getComplianceAutomation(previousAutomationName);
        const automationNameForUpdate = previousAutomationName;
        const compensations = new CompensationStack();

        const resolvedTriggerId = await this.saveTriggerForPopup({
            editingTriggerId: formState.editingTriggerId,
            previousPopup,
            previousAutomationName,
            triggerSettings,
            automationName: automationNameForUpdate,
            riskLevel,
            active: payload.active,
            compensations,
        });

        if (resolvedTriggerId === null) {
            await this.runRollback(
                compensations,
                'Could not update trigger for this Pop-Up. Changes were rolled back.'
            );
            return false;
        }

        const automationDto = buildMarkersOfHarmAutomationDto({
            automationName: automationNameForUpdate,
            popupReference: createPopupReference(resolvedTriggerId),
            popupTitle: payload.title,
            active: payload.active,
            appliedActionSettings,
        });

        if (automationDto === null) {
            this.setError('Applied action configuration is incomplete.');
            await this.runRollback(
                compensations,
                'Could not build automation for this Pop-Up. Changes were rolled back.'
            );
            return false;
        }

        const automationSaved = await this.upsertComplianceAutomation(previousAutomation, automationDto, compensations);
        if (!automationSaved) {
            await this.runRollback(
                compensations,
                'Could not update compliance automation for this Pop-Up. Changes were rolled back.'
            );
            return false;
        }

        const updatedPopup = await this.persistUpdatePopup(popupId, {
            ...payload,
            triggerId: resolvedTriggerId,
        });

        if (updatedPopup === null) {
            await this.runRollback(compensations, 'Could not update Pop-Up. Changes were rolled back.');
            return false;
        }

        await this.refreshPopups();

        const refreshed = this.popups.find((popup) => popup.id === updatedPopup.id);
        if (refreshed !== undefined) {
            const automation = await this.getComplianceAutomation(automationNameForUpdate);
            formState.loadFromPopup(refreshed);
            if (automation !== null) {
                formState.loadAppliedActionSettings(hydrateAppliedActionSettingsFromAutomation(automation));
            }
            formState.editingAutomationName = automationNameForUpdate;
            formState.markCurrentStateAsSaved();
            this.selectPopupForEdit(refreshed.id);
        }

        formState.editingTriggerId = resolvedTriggerId;

        return true;
    };
}
